import {
  type FriendsDto,
  type HeartPackDto,
  isVipActive,
  type ReferralDto,
  type ShopDto,
  type TransferResultDto,
  VIP_PLANS,
  type VipStatusDto,
  type WalletDto,
} from '@spin/shared';
import { useEffect, useState } from 'react';
import { clientId, http } from '../api/http';
import { Dialog } from '../components/Dialog';
import { Avatar, Spinner } from '../components/common';
import { CloseIcon, GmCoin, PriceHeart } from '../components/icons';
import { PackArt } from '../components/PackArt';
import { reportError, useT } from '../i18n';
import { useSession } from '../store/session';
import { useUi } from '../store/ui';
import { tg } from '../tg/bridge';

function Ribbon({ kind }: { kind: HeartPackDto['badge'] }) {
  const t = useT();
  if (!kind) return null;
  return <span className={`ribbon ${kind}`}>{t(`shop.${kind}`)}</span>;
}

/**
 * "Buy hearts": VIP and the invite reward on top, then heart packs for GM, then the two "Send" tiles. GM is the
 * portal currency an admin credits; the server swaps it for hearts.
 */
export function ShopDialog() {
  const t = useT();
  const { openDialog } = useUi.getState();
  const [shop, setShop] = useState<ShopDto | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    http.get<ShopDto>('/shop').then(setShop).catch(reportError);
  }, []);

  const buy = async (pack: HeartPackDto) => {
    if (!shop) return;
    if (pack.gm > shop.gm) {
      useUi.getState().toast(t('shop.need_gm', { gm: pack.gm }), 'error');
      return;
    }
    setBusy(pack.code);
    try {
      const wallet = await http.post<WalletDto>('/shop/hearts', {
        packCode: pack.code,
        clientTxId: clientId(),
      });
      useSession.getState().setWallet(wallet);
      setShop({ ...shop, ...wallet });
      tg.haptic('success');
      useUi.getState().toast(t('shop.bought', { hearts: pack.hearts }), 'success');
    } catch (err) {
      reportError(err);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="shop-layer"
      onClick={(e) => e.target === e.currentTarget && useUi.getState().closeDialog()}
    >
      <div className="shop" role="dialog" data-testid="shop">
        <button
          className="shop-close"
          onClick={() => useUi.getState().closeDialog()}
          aria-label={t('common.close')}
          data-testid="dialog-close"
        >
          <CloseIcon />
        </button>
        <div className="shop-title">{t('shop.title')}</div>
        {shop && (
          <div className="shop-balance" data-testid="shop-gm">
            <GmCoin size={14} /> {shop.gm}
          </div>
        )}
        {!shop ? (
          <Spinner />
        ) : (
          <div className="shop-grid">
            <div className="shop-tile">
              <span className="tile-head vip-head">
                <b>VIP</b>
                <small>{t('shop.vip_status')}</small>
              </span>
              <span className="tile-art">
                <PackArt code="vip" />
              </span>
              <button
                className="tile-btn blue"
                onClick={() => openDialog('vip', undefined, { stack: true })}
                data-testid="vip-details"
              >
                {t('shop.details')}
              </button>
            </div>
            <div className="shop-tile">
              <span className="tile-head small">
                {t('shop.for_friend_1')} <PriceHeart />
                <b>{shop.referralBonus}</b> {t('shop.for_friend_2')}
              </span>
              <span className="tile-art">
                <PackArt code="friend" />
              </span>
              <button
                className="tile-btn blue"
                onClick={() => openDialog('referral', undefined, { stack: true })}
              >
                {t('shop.details')}
              </button>
            </div>
            {shop.packs.map((p) => (
              <div key={p.code} className="shop-tile">
                <Ribbon kind={p.badge} />
                <span className="tile-head">
                  <span className="hearts-amount">
                    <PriceHeart />
                    {p.hearts}
                  </span>
                  {p.bonusPercent > 0 && <small>{t('shop.bonus', { percent: p.bonusPercent })}</small>}
                </span>
                <span className="tile-art">
                  <PackArt code={p.code} />
                </span>
                <button
                  className="tile-btn green"
                  disabled={busy !== null}
                  onClick={() => void buy(p)}
                  data-testid={`buy-${p.code}`}
                >
                  {p.gm} <GmCoin size={14} />
                </button>
              </div>
            ))}
            <div className="shop-tile">
              <span className="tile-head">
                <span className="hearts-amount">
                  <GmCoin size={15} />
                  {shop.transfer.gm}
                </span>
              </span>
              <span className="tile-art">
                <PackArt code="bunny" />
              </span>
              <button
                className="tile-btn green"
                onClick={() => openDialog('send', { currency: 'GM' }, { stack: true })}
                data-testid="send-gm"
              >
                {t('shop.send')}
              </button>
            </div>
            <div className="shop-tile">
              <span className="tile-head">
                <span className="hearts-amount">
                  <PriceHeart />
                  {shop.transfer.hearts}
                </span>
              </span>
              <span className="tile-art">
                <PackArt code="bunny" />
              </span>
              <button
                className="tile-btn green"
                onClick={() => openDialog('send', { currency: 'HEARTS' }, { stack: true })}
                data-testid="send-hearts"
              >
                {t('shop.send')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** VIP status: what it gives and the plans, paid with hearts. */
export function VipDialog() {
  const t = useT();
  const me = useSession((s) => s.me)!;
  const [busy, setBusy] = useState<string | null>(null);
  const active = isVipActive(me.vipUntil);
  const date = (iso: string) =>
    new Date(iso).toLocaleDateString(me.locale, { day: 'numeric', month: 'long' });

  const buy = async (plan: string) => {
    setBusy(plan);
    try {
      const res = await http.post<VipStatusDto>('/vip/buy', { plan, clientTxId: clientId() });
      useSession.getState().setMe({ ...me, vipUntil: res.vipUntil, balance: res.balance });
      tg.haptic('success');
      if (res.vipUntil) useUi.getState().toast(t('vip.bought', { date: date(res.vipUntil) }), 'success');
    } catch (err) {
      reportError(err);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog title={t('vip.title')} centered testId="vip">
      <div className="vip-card">
        <span className="vip-art">
          <PackArt code="vip" />
        </span>
        <p className={active ? 'vip-state on' : 'vip-state'}>
          {active && me.vipUntil ? t('vip.active_until', { date: date(me.vipUntil) }) : t('vip.inactive')}
        </p>
        <p className="vip-perks">{t('vip.perks')}</p>
        <div className="vip-plans">
          {VIP_PLANS.map((plan) => (
            <button
              key={plan.code}
              className="btn btn-green"
              disabled={busy !== null}
              onClick={() => void buy(plan.code)}
              data-testid={`buy-${plan.code}`}
            >
              {t('vip.plan', { days: plan.days })} · <PriceHeart /> {plan.price}
            </button>
          ))}
        </div>
      </div>
    </Dialog>
  );
}

/** "Get ❤20 for a friend": the invite link, shared only when the player taps. */
export function ReferralDialog() {
  const t = useT();
  const [ref, setRef] = useState<ReferralDto | null>(null);
  useEffect(() => {
    http.get<ReferralDto>('/referral').then(setRef).catch(reportError);
  }, []);
  return (
    <Dialog title={t('invite.title')} centered testId="referral">
      {!ref ? (
        <Spinner />
      ) : (
        <div className="referral-card">
          <span className="vip-art">
            <PackArt code="friend" />
          </span>
          <p>{t('invite.text', { bonus: ref.bonus })}</p>
          <button
            className="btn btn-blue btn-block"
            onClick={() => tg.share(ref.link, t('invite.share_text'))}
          >
            {t('invite.share')}
          </button>
          <button
            className="btn btn-block"
            onClick={() =>
              void navigator.clipboard
                ?.writeText(ref.link)
                .then(() => useUi.getState().toast(t('invite.copied'), 'success'))
                .catch(() => undefined)
            }
          >
            {t('invite.copy')}
          </button>
          <p className="muted">{t('invite.stats', { invited: ref.invited, rewarded: ref.rewarded })}</p>
        </div>
      )}
    </Dialog>
  );
}

/** The "Send" tiles: GM 20 or ❤20 to one of my friends. */
export function SendDialog({ currency }: { currency: 'GM' | 'HEARTS' }) {
  const t = useT();
  const [friends, setFriends] = useState<FriendsDto | null>(null);
  const [shop, setShop] = useState<ShopDto | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    http.get<FriendsDto>('/friends').then(setFriends).catch(reportError);
    http.get<ShopDto>('/shop').then(setShop).catch(reportError);
  }, []);
  const amount = shop ? (currency === 'GM' ? shop.transfer.gm : shop.transfer.hearts) : 0;

  const send = async (toUserId: string, name: string) => {
    setBusy(true);
    try {
      const res = await http.post<TransferResultDto>('/shop/send', {
        currency,
        toUserId,
        clientTxId: clientId(),
      });
      useSession.getState().setWallet({ balance: res.balance, gm: res.gm });
      setShop((s) =>
        s
          ? { ...s, balance: res.balance, gm: res.gm, transfer: { ...s.transfer, leftToday: res.leftToday } }
          : s,
      );
      tg.haptic('success');
      useUi
        .getState()
        .toast(t(currency === 'GM' ? 'shop.sent_gm' : 'shop.sent_hearts', { amount, name }), 'success');
    } catch (err) {
      reportError(err);
    } finally {
      setBusy(false);
    }
  };

  const list = friends?.friends ?? [];
  return (
    <Dialog
      title={t(currency === 'GM' ? 'shop.send_gm_title' : 'shop.send_hearts_title', { amount })}
      centered
      testId="send"
    >
      {!friends || !shop ? (
        <Spinner />
      ) : list.length === 0 ? (
        <p className="empty">{t('shop.send_no_friends')}</p>
      ) : (
        <>
          <p className="muted send-left">{t('shop.send_left', { count: shop.transfer.leftToday })}</p>
          <div className="send-list">
            {list.map((f) => (
              <div key={f.userId} className="send-row">
                <Avatar name={f.displayName} photoUrl={f.photoUrl} size={34} />
                <span className="ellipsis">{f.displayName}</span>
                <button
                  className="btn btn-green"
                  disabled={busy || shop.transfer.leftToday < 1}
                  onClick={() => void send(f.userId, f.displayName)}
                  data-testid={`send-to-${f.userId}`}
                >
                  {t('shop.send')}
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </Dialog>
  );
}
